import React, { FC, useEffect, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import styles from './Cart.module.scss';
import { useCart } from '../../../utils/hook/useCart';
import { useAppSelector } from '../../../utils/hook/redux-hooks';
import { localizeDish } from '../../../utils/localization/apply';
import { useUiString } from '../../../utils/hook/useUiString';
import { useIsDesktop } from '../../../utils/hook/useIsDesktop';
import { ActiveOrderApi } from '../../../utils/hook/useActiveOrder';
import { useFulfillment } from '../../../utils/hook/useFulfillment';
import { CheckoutForm } from './CheckoutForm';
import { PaymentStep } from './PaymentStep';
import { OrderStatusStep } from './OrderStatusStep';
import { QuantityControl } from './QuantityControl';
import { ReactComponent as CloseIcon } from '../../../assets/icons/close.svg';
import { ReactComponent as CartIcon } from '../../../assets/icons/cart-icon.svg';

type View = 'cart' | 'checkout' | 'pay' | 'status';

interface IProps {
    open: boolean;
    onClose: () => void;
    /** The order in flight, if there is one. Owned by CartLayer so the
     *  floating button and this drawer never disagree about it. */
    activeOrder: ActiveOrderApi;
}

interface LinePresentation {
    name: string;
    image: string | null;
}

function money(amount: number): string {
    return `$${amount.toFixed(2)}`;
}

export const CartDrawer: FC<IProps> = ({ open, onClose, activeOrder }) => {
    const { menuId, items, total, clear } = useCart();
    const { menu, locale } = useAppSelector((state) => state.menu);
    const t = useUiString();
    const fulfillment = useFulfillment();
    const { active, start, settle, dismiss } = activeOrder;

    // A basket line only stores what the order needs. The menu on screen
    // supplies the localized name and the picture.
    const present = (dishId: number, fallback: string): LinePresentation => {
        const dish = menu?.dishes?.find((entry) => entry.id === dishId);
        if (!dish) return { name: fallback, image: null };
        return {
            name:  localizeDish(dish, locale).name,
            image: dish.image?.formats?.small?.url ?? dish.image?.url ?? null,
        };
    };
    const [view, setView] = useState<View>('cart');
    const isDesktop = useIsDesktop();

    // Reopening lands wherever the guest left off. An unpaid card comes
    // first — that order is not finished. Otherwise a running order only
    // takes over the drawer while the basket is empty, so a guest who has
    // started picking a second round is not trapped on a status screen.
    useEffect(() => {
        if (!open) return;
        if (active?.payment) { setView('pay'); return; }
        setView(active && items.length === 0 ? 'status' : 'cart');
    }, [open, active, items.length]);

    const finishPayment = () => {
        settle();
        clear();
        setView('status');
    };

    const closeAndForget = () => {
        dismiss();
        onClose();
    };

    return (
        <Drawer
            anchor={isDesktop ? 'right' : 'bottom'}
            open={open}
            onClose={onClose}
            ModalProps={{
                BackdropProps: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' } },
            }}
            PaperProps={{
                className: isDesktop ? styles.paperRight : styles.paperBottom,
                sx: { backgroundColor: 'transparent', boxShadow: 'none' },
            }}
        >
            <div className={styles.sheet}>
                {!isDesktop && <div className={styles.handle} />}
                {view === 'status' && active ? (
                    <div key="status" className={styles.sheetBody}>
                        <OrderStatusStep
                            orderId={active.orderId}
                            method={active.method}
                            onDismiss={closeAndForget}
                            onClose={onClose}
                        />
                    </div>
                ) : view === 'pay' && active?.payment ? (
                    <div key="pay" className={styles.sheetBody}>
                        <PaymentStep
                            orderId={active.orderId}
                            payment={active.payment}
                            onPaid={finishPayment}
                            onClose={onClose}
                        />
                    </div>
                ) : view === 'checkout' && menuId !== null ? (
                    <div key="checkout" className={styles.sheetBody}>
                        <CheckoutForm
                            menuId={menuId}
                            onBack={() => setView('cart')}
                            onClose={onClose}
                            onSuccess={(confirmation) => {
                                // The server's own answer wins: it wrote the
                                // row. The local rule is only a fallback for
                                // an older backend that does not report one.
                                start({
                                    orderId: confirmation.id,
                                    method:  confirmation.payment_method ?? fulfillment,
                                    payment: confirmation.payment ?? null,
                                });
                                setView(confirmation.payment ? 'pay' : 'status');
                            }}
                        />
                    </div>
                ) : (
                    <div key="cart" className={styles.sheetBody}>
                        <div className={styles.sheetHeader}>
                            <span className={styles.headerSlot} />
                            <h4 className={styles.sheetTitle}>{t('yourOrder')}</h4>
                            <button
                                type="button"
                                className={styles.iconButton}
                                onClick={onClose}
                                aria-label={t('closeCart')}
                            >
                                <CloseIcon className={styles.iconSvg} />
                            </button>
                        </div>

                        {items.length === 0 ? (
                            <div className={styles.emptyState}>
                                <span className={styles.emptyIcon}>
                                    <CartIcon />
                                </span>
                                <p className={styles.empty}>{t('yourCartIsEmpty')}</p>
                            </div>
                        ) : (
                            <>
                                <div className={styles.scroll}>
                                    <div className={styles.lines}>
                                        {items.map((item, index) => {
                                            const { name, image } = present(item.dishId, item.name);
                                            return (
                                                <div
                                                    key={item.dishId}
                                                    className={styles.line}
                                                    style={{ animationDelay: `${index * 40}ms` }}
                                                >
                                                    {image ? (
                                                        <img
                                                            className={styles.thumb}
                                                            src={image}
                                                            alt=""
                                                            loading="lazy"
                                                        />
                                                    ) : (
                                                        <span className={styles.thumb} />
                                                    )}
                                                    <div className={styles.lineMeta}>
                                                        <span className={styles.lineName}>{name}</span>
                                                        <span className={styles.lineDetail}>
                                                            {money(item.price)} × {item.quantity}
                                                        </span>
                                                    </div>
                                                    <div className={styles.lineSide}>
                                                        <span className={styles.linePrice}>
                                                            {money(item.price * item.quantity)}
                                                        </span>
                                                        <QuantityControl
                                                            dish={{
                                                                id: item.dishId,
                                                                name: item.name,
                                                                price: item.price,
                                                            }}
                                                            variant="drawer"
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className={styles.sheetFooter}>
                                    <div className={styles.total}>
                                        <span>{t('total')}</span>
                                        <span>{money(total)}</span>
                                    </div>
                                    {/* The basket total is pre-tax on every
                                        route: the venue's till and Square both
                                        apply tax after this screen. */}
                                    <p className={styles.disclaimer}>{t('priceDisclaimer')}</p>
                                    <button
                                        type="button"
                                        className={styles.primaryButton}
                                        onClick={() => setView('checkout')}
                                        disabled={menuId === null}
                                    >
                                        {t('checkout')} · {money(total)}
                                    </button>
                                    {active && (
                                        <button
                                            type="button"
                                            className={styles.secondaryButton}
                                            onClick={() => setView('status')}
                                        >
                                            <span className={styles.liveDot} />
                                            {t('yourOrder')}
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </Drawer>
    );
};
